import { inject, Injectable } from '@angular/core';
import { collection, doc, DocumentData, Query, query, where, writeBatch } from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytesResumable, UploadTask } from 'firebase/storage';
import moment from 'moment';
import { Auth, authState } from '../firebase/auth';
import { Firestore } from '../firebase/firestore';
import { Storage } from '../firebase/storage';

@Injectable({
  providedIn: 'root',
})
export class FirebaseService {
  private auth = inject(Auth);
  private firestore = inject(Firestore);
  private storage: Storage = inject(Storage);

  getAuthState = () => {
    return authState(this.auth);
  };

  getAuthUser = async () => {
    return this.auth.currentUser;
  };

  getColRef = (colPath: string) => {
    return collection(this.firestore, colPath);
  };

  getDocRef = (colPath: string, docId?: string) => {
    const colRef = this.getColRef(colPath);
    return docId ? doc(colRef, docId) : doc(colRef);
  };

  getWriteBatch = () => {
    return writeBatch(this.firestore);
  };

  getQueryWithFilters = (q: Query<DocumentData, DocumentData>, filter: FilterParams) => {
    if (filter.startDate) {
      const startDate = moment(filter.startDate).startOf('day').toDate();
      q = query(q, where(filter.filterByDate, '>=', startDate));
    }
    if (filter.endDate) {
      const endDate = moment(filter.endDate).endOf('day').toDate();
      q = query(q, where(filter.filterByDate, '<=', endDate));
    }
    if (filter.searchTerm) {
      q = query(
        q,
        where(filter.property, '>=', filter.searchTerm),
        where(filter.property, '<=', filter.searchTerm + '\uf8ff'),
      );
    }
    return q;
  };

  getUploadTaskDownloadURL = async (task: UploadTask) => {
    const downloadUrl = await getDownloadURL((await task).ref);
    return downloadUrl;
  };

  doUploadFile = async (myFile: File, path: string) => {
    const storageRef = ref(this.storage, `${path}/${myFile.name}`);
    const task = uploadBytesResumable(storageRef, myFile);
    const downloadUrl = await getDownloadURL((await task).ref);
    return downloadUrl;
  };

  doStartUploadTask = (myFile: File, path: string) => {
    const storageRef = ref(this.storage, `${path}/${myFile.name}`);
    return uploadBytesResumable(storageRef, myFile);
  };
}

class FilterParams {
  property: string;
  searchTerm: string = null;
  filterByDate: string;
  startDate: string;
  endDate: string;
  max?: number;
  sort?: string;
  order?: 'asc' | 'desc'; // desc, asc
}
